import { Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../utils/cn';

type AudioPlayerProps = {
  src: string;
  mimeType: string;
  durationSeconds?: number | null;
  className?: string;
};

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}

export const AudioPlayer = ({ src, mimeType, durationSeconds, className }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const isResolvingDuration = useRef(false);
  const seekedCleanupRef = useRef<(() => void) | null>(null);
  const resolveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);
  const [isMuted, setIsMuted] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    const onDurationChange = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      seekedCleanupRef.current?.();
    };
  }, []);

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration);
    } else if (duration > 0) {
      // Backend already provided the duration — skip the seek trick.
      return;
    } else {
      // For formats like OGG where duration is Infinity until fully buffered,
      // seek to a large time to force the browser to determine the real duration.
      const finish = () => {
        if (isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
        audio.currentTime = 0;
        audio.removeEventListener('seeked', finish);
        if (resolveTimeoutRef.current) clearTimeout(resolveTimeoutRef.current);
        resolveTimeoutRef.current = null;
        isResolvingDuration.current = false;
        seekedCleanupRef.current = null;
      };
      seekedCleanupRef.current = () => {
        audio.removeEventListener('seeked', finish);
        if (resolveTimeoutRef.current) clearTimeout(resolveTimeoutRef.current);
      };
      isResolvingDuration.current = true;
      audio.addEventListener('seeked', finish);
      // Safety net: if `seeked` never fires (e.g. upstream ignores range
      // requests), don't leave the play button permanently disabled.
      resolveTimeoutRef.current = setTimeout(finish, 3000);
      audio.currentTime = 1e10;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || isResolvingDuration.current) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(!isMuted);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !isFinite(duration) || !duration || isResolvingDuration.current) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = fraction * duration;
    setCurrentTime(audio.currentTime);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 bg-card border border-border rounded-xl px-3 py-2 w-full min-w-[220px]',
        className,
      )}
      data-testid="attachment-audio"
    >
      <audio
        ref={audioRef}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      >
        <source src={src} type={mimeType} />
      </audio>

      <button
        type="button"
        onClick={togglePlay}
        className="flex items-center justify-center h-8 w-8 rounded-full bg-foreground text-white hover:bg-foreground/90 transition-colors shrink-0"
        data-testid="audio-play-button"
      >
        {isPlaying ? (
          <Pause className="h-3.5 w-3.5 fill-white" />
        ) : (
          <Play className="h-3.5 w-3.5 ml-0.5 fill-white" />
        )}
      </button>

      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <div
          ref={progressRef}
          className="cursor-pointer group py-1.5"
          onClick={handleProgressClick}
          data-testid="audio-progress-bar"
        >
          <div className="relative h-1 bg-accent rounded-full">
            <div
              className="absolute left-0 top-0 h-full bg-foreground rounded-full transition-[width] duration-100"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 h-3 w-3 bg-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>
        </div>
        <div className="flex justify-between">
          <span className="text-xs text-foreground-subtle tabular-nums">
            {formatTime(currentTime)}
          </span>
          <span className="text-xs text-foreground-subtle tabular-nums">
            {formatTime(duration)}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={toggleMute}
        className="text-foreground-subtle hover:text-muted-foreground transition-colors shrink-0"
        data-testid="audio-mute-button"
      >
        {isMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};
